import { useState } from 'react';
import { useLang } from '../../../hooks/useLang';

interface Question {
  id: string;
  zh: string;
  en: string;
  options: { value: string; zh: string; en: string; score: Record<string, number> }[];
}

const QUESTIONS: Question[] = [
  {
    id: 'asset_type',
    zh: '您的资产类型是？',
    en: 'What type of asset do you have?',
    options: [
      { value: 'realestate', zh: '房地产（住宅/商业/工业）', en: 'Real Estate (residential/commercial/industrial)', score: { feasibility: 2, hk: 2, complexity: 1 } },
      { value: 'bond', zh: '债券/票据/应收账款', en: 'Bonds / Notes / Receivables', score: { feasibility: 3, hk: 2, complexity: 0 } },
      { value: 'equity', zh: '股权/基金份额', en: 'Equity / Fund Shares', score: { feasibility: 2, hk: 2, complexity: 2 } },
      { value: 'carbon', zh: '碳信用/ESG资产', en: 'Carbon Credits / ESG Assets', score: { feasibility: 3, hk: 3, complexity: 0 } },
      { value: 'supplychain', zh: '供应链金融/应收账款', en: 'Supply Chain Finance / Receivables', score: { feasibility: 3, hk: 3, complexity: 0 } },
      { value: 'energy', zh: '新能源/光伏/风电', en: 'Clean Energy / Solar / Wind', score: { feasibility: 3, hk: 3, complexity: 0 } },
      { value: 'commodity', zh: '大宗商品（黄金/石油等）', en: 'Commodities (Gold / Oil etc.)', score: { feasibility: 2, hk: 1, complexity: 1 } },
      { value: 'art', zh: '艺术品/收藏品', en: 'Art / Collectibles', score: { feasibility: 1, hk: 1, complexity: 2 } },
    ],
  },
  {
    id: 'asset_size',
    zh: '资产规模大约是？',
    en: 'What is the approximate asset size?',
    options: [
      { value: 'small', zh: '500万港元以下', en: 'Below HKD 5M', score: { feasibility: 0, hk: 0, complexity: 0 } },
      { value: 'medium', zh: '500万 - 5000万港元', en: 'HKD 5M - 50M', score: { feasibility: 1, hk: 1, complexity: 0 } },
      { value: 'large', zh: '5000万 - 5亿港元', en: 'HKD 50M - 500M', score: { feasibility: 2, hk: 2, complexity: 0 } },
      { value: 'xlarge', zh: '5亿港元以上', en: 'Above HKD 500M', score: { feasibility: 3, hk: 3, complexity: 0 } },
    ],
  },
  {
    id: 'title_status',
    zh: '资产权属状态如何？',
    en: 'What is the asset title status?',
    options: [
      { value: 'clear', zh: '产权清晰，无抵押/查封', en: 'Clear title, no mortgage/seizure', score: { feasibility: 3, hk: 2, complexity: 0 } },
      { value: 'partial', zh: '部分抵押，可协商解除', en: 'Partially mortgaged, negotiable', score: { feasibility: 1, hk: 1, complexity: 1 } },
      { value: 'complex', zh: '产权复杂，存在争议', en: 'Complex title, disputes exist', score: { feasibility: 0, hk: 0, complexity: 3 } },
      { value: 'unknown', zh: '尚未完成确权', en: 'Title not yet confirmed', score: { feasibility: 0, hk: 0, complexity: 2 } },
    ],
  },
  {
    id: 'cashflow',
    zh: '资产是否有稳定现金流？',
    en: 'Does the asset generate stable cash flow?',
    options: [
      { value: 'stable', zh: '有稳定租金/利息/分红收益', en: 'Stable rent / interest / dividend income', score: { feasibility: 3, hk: 2, complexity: 0 } },
      { value: 'irregular', zh: '有收益但不稳定', en: 'Has income but irregular', score: { feasibility: 2, hk: 1, complexity: 1 } },
      { value: 'appreciation', zh: '主要靠升值，无稳定现金流', en: 'Mainly appreciation, no stable cash flow', score: { feasibility: 1, hk: 1, complexity: 2 } },
      { value: 'none', zh: '暂无收益（开发中/储备资产）', en: 'No income yet (under development)', score: { feasibility: 0, hk: 0, complexity: 2 } },
    ],
  },
  {
    id: 'compliance_ready',
    zh: '您的合规准备程度？',
    en: 'What is your compliance readiness?',
    options: [
      { value: 'ready', zh: '已有律师团队，了解香港SFC要求', en: 'Have legal team, familiar with HK SFC requirements', score: { feasibility: 3, hk: 3, complexity: 0 } },
      { value: 'partial', zh: '有初步了解，需要专业指导', en: 'Basic understanding, need professional guidance', score: { feasibility: 2, hk: 2, complexity: 0 } },
      { value: 'learning', zh: '刚开始了解RWA合规', en: 'Just starting to learn RWA compliance', score: { feasibility: 1, hk: 1, complexity: 1 } },
      { value: 'none', zh: '完全不了解合规要求', en: 'No knowledge of compliance requirements', score: { feasibility: 0, hk: 0, complexity: 2 } },
    ],
  },
];

interface Result {
  feasibility: number;
  hk: number;
  complexity: number;
}

function getRecommendation(result: Result, lang: string) {
  const t = (zh: string, en: string) => lang === 'zh' ? zh : en;
  const total = result.feasibility + result.hk;

  if (result.complexity >= 5) {
    return {
      level: 'caution',
      color: '#EF4444',
      icon: 'ri-error-warning-line',
      title: t('暂不建议立即推进', 'Not Recommended to Proceed Now'),
      desc: t(
        '您的资产存在较高的合规复杂度（产权争议、规模过小或无稳定现金流），建议先完成资产确权、解决产权纠纷，再评估RWA可行性。',
        'Your asset has high compliance complexity (title disputes, too small, or no stable cash flow). Recommend completing title confirmation and resolving disputes first.'
      ),
      paths: [],
      nextSteps: [
        t('完成资产法律确权（委托律师出具产权意见书）', 'Complete legal title confirmation (engage lawyer for title opinion)'),
        t('解决产权争议或抵押问题', 'Resolve title disputes or mortgage issues'),
        t('建立稳定的资产收益模型', 'Establish stable asset income model'),
        t('了解香港SFC合规基础要求', 'Learn HK SFC basic compliance requirements'),
      ],
    };
  }

  if (total >= 10) {
    return {
      level: 'excellent',
      color: '#10B981',
      icon: 'ri-checkbox-circle-fill',
      title: t('非常适合推进RWA！', 'Excellent RWA Candidate!'),
      desc: t(
        '您的资产条件优秀，高度符合香港RWA合规发行要求。建议尽快启动正式评估流程，对接SFC持牌机构。',
        'Your asset conditions are excellent and highly suitable for HK RWA compliant issuance. Recommend starting formal evaluation and engaging SFC-licensed institutions soon.'
      ),
      paths: [
        { name: t('香港SPV发行路径', 'HK SPV Issuance Path'), desc: t('境内确权 → 证监会备案 → 香港SPV → SFC持牌机构发行', 'Mainland title → CSRC filing → HK SPV → SFC-licensed issuance'), color: '#10B981' },
        { name: t('2月6日新政优先赛道', 'Feb 6 Policy Priority Track'), desc: t('供应链金融/新能源/碳资产享有政策优先支持', 'Supply chain / clean energy / carbon assets have policy priority'), color: '#F59E0B' },
      ],
      nextSteps: [
        t('委托香港律师出具资产法律意见书', 'Engage HK lawyer for asset legal opinion'),
        t('对接SFC持牌1类/9类机构（HashKey/OSL等）', 'Connect with SFC-licensed Type 1/9 institutions (HashKey/OSL etc.)'),
        t('启动证监会跨境RWA备案流程', 'Initiate CSRC cross-border RWA filing process'),
        t('设立开曼/BVI SPV结构', 'Establish Cayman/BVI SPV structure'),
      ],
    };
  }

  if (total >= 6) {
    return {
      level: 'good',
      color: '#F59E0B',
      icon: 'ri-thumb-up-line',
      title: t('具备RWA潜力，需要准备', 'RWA Potential, Preparation Needed'),
      desc: t(
        '您的资产具备RWA化的基本条件，但还需要在合规准备、资产确权或规模方面进一步完善，建议先做好基础工作再推进。',
        'Your asset has basic RWA potential, but needs further work on compliance preparation, title confirmation, or scale. Recommend completing groundwork first.'
      ),
      paths: [
        { name: t('分阶段推进策略', 'Phased Approach Strategy'), desc: t('先完善合规基础，再启动正式发行流程', 'Complete compliance foundation first, then start formal issuance'), color: '#F59E0B' },
      ],
      nextSteps: [
        t('完善资产确权文件（产权证、评估报告）', 'Complete title documents (property certificate, valuation report)'),
        t('了解香港专业投资者（PI）认定标准', 'Learn HK Professional Investor (PI) qualification standards'),
        t('初步咨询SFC持牌机构的发行可行性', 'Initial consultation with SFC-licensed institutions on issuance feasibility'),
        t('评估合规成本（50-200万港元区间）', 'Evaluate compliance costs (HKD 500K-2M range)'),
      ],
    };
  }

  return {
    level: 'early',
    color: '#6B7280',
    icon: 'ri-time-line',
    title: t('尚处早期阶段，建议先学习', 'Early Stage — Recommend Learning First'),
    desc: t(
      '您的资产目前不完全符合RWA发行条件，建议先系统学习RWA合规知识，完善资产基础条件后再评估。',
      'Your asset does not fully meet RWA issuance conditions yet. Recommend systematically learning RWA compliance knowledge and improving asset conditions first.'
    ),
    paths: [],
    nextSteps: [
      t('系统学习本站RWA词汇表和合规指南', 'Systematically study this site\'s RWA glossary and compliance guide'),
      t('了解香港RWA监管框架基础', 'Learn HK RWA regulatory framework basics'),
      t('评估资产是否满足最低规模要求（建议≥5000万港元）', 'Evaluate if asset meets minimum scale (recommend ≥HKD 50M)'),
      t('关注2月6日新政的优先支持赛道', 'Follow Feb 6 policy priority tracks'),
    ],
  };
}

export default function RWASelfTest() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<Result | null>(null);
  const { t, lang } = useLang();

  const currentQ = QUESTIONS[step];

  const handleAnswer = (value: string) => {
    const q = QUESTIONS[step];
    const option = q.options.find((o) => o.value === value)!;
    const newAnswers = { ...answers, [q.id]: value };
    setAnswers(newAnswers);

    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      // Calculate result
      const totals: Result = { feasibility: 0, hk: 0, complexity: 0 };
      QUESTIONS.forEach((q) => {
        const ans = newAnswers[q.id];
        const opt = q.options.find((o) => o.value === ans);
        if (opt) {
          totals.feasibility += opt.score.feasibility ?? 0;
          totals.hk += opt.score.hk ?? 0;
          totals.complexity += opt.score.complexity ?? 0;
        }
      });
      setResult(totals);
    }
  };

  const reset = () => {
    setStep(0);
    setAnswers({});
    setResult(null);
  };

  const recommendation = result ? getRecommendation(result, lang) : null;

  return (
    <>
      {/* Trigger button */}
      <div className="mt-8 flex justify-center">
        <button
          onClick={() => { setOpen(true); reset(); }}
          className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full font-bold text-sm cursor-pointer transition-all hover:scale-105 whitespace-nowrap"
          style={{ background: 'linear-gradient(135deg, #7C3AED, #A78BFA)', color: '#fff', boxShadow: '0 4px 20px rgba(124,58,237,0.3)' }}
        >
          <i className="ri-questionnaire-line text-base"></i>
          {t('企业RWA适合度自测（5题）', 'Enterprise RWA Suitability Self-Test (5 Questions)')}
          <i className="ri-arrow-right-line text-sm"></i>
        </button>
      </div>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }}>
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #1A0050, #2E1065)' }}>
              <div className="flex items-center gap-2">
                <i className="ri-questionnaire-line text-[#A78BFA] text-lg"></i>
                <span className="text-white font-black text-base">{t('企业RWA适合度自测', 'Enterprise RWA Suitability Test')}</span>
              </div>
              <button onClick={() => setOpen(false)} className="w-7 h-7 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 cursor-pointer transition-colors">
                <i className="ri-close-line text-white text-sm"></i>
              </button>
            </div>

            {!result ? (
              <div className="p-6">
                {/* Progress */}
                <div className="flex items-center gap-3 mb-5">
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{ width: `${((step) / QUESTIONS.length) * 100}%`, background: 'linear-gradient(90deg, #7C3AED, #A78BFA)' }}
                    />
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">{step + 1} / {QUESTIONS.length}</span>
                </div>

                {/* Question */}
                <h3 className="font-black text-gray-800 text-base mb-4">
                  {lang === 'zh' ? currentQ.zh : currentQ.en}
                </h3>

                {/* Options */}
                <div className="space-y-2">
                  {currentQ.options.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => handleAnswer(opt.value)}
                      className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-700 hover:border-[#7C3AED] hover:bg-[#7C3AED]/5 transition-all cursor-pointer font-medium"
                    >
                      {lang === 'zh' ? opt.zh : opt.en}
                    </button>
                  ))}
                </div>

                {step > 0 && (
                  <button
                    onClick={() => setStep(step - 1)}
                    className="mt-4 text-xs text-gray-400 hover:text-gray-600 cursor-pointer flex items-center gap-1"
                  >
                    <i className="ri-arrow-left-line"></i>
                    {t('上一题', 'Previous')}
                  </button>
                )}
              </div>
            ) : (
              <div className="p-6">
                {/* Result */}
                <div
                  className="rounded-2xl p-5 mb-5 text-center"
                  style={{ background: `${recommendation!.color}10`, border: `2px solid ${recommendation!.color}30` }}
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full mx-auto mb-3" style={{ background: recommendation!.color }}>
                    <i className={`${recommendation!.icon} text-white text-xl`}></i>
                  </div>
                  <h3 className="font-black text-gray-800 text-base mb-2">{recommendation!.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{recommendation!.desc}</p>
                </div>

                {/* Score bars */}
                <div className="grid grid-cols-3 gap-3 mb-5">
                  {[
                    { label: t('可行性', 'Feasibility'), value: result.feasibility, max: 15, color: '#10B981' },
                    { label: t('香港路径', 'HK Path'), value: result.hk, max: 13, color: '#7C3AED' },
                    { label: t('复杂度', 'Complexity'), value: result.complexity, max: 10, color: '#EF4444', reverse: true },
                  ].map((bar) => (
                    <div key={bar.label} className="text-center">
                      <div className="text-xs text-gray-500 mb-1">{bar.label}</div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-1">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${Math.min(100, (bar.value / bar.max) * 100)}%`,
                            background: bar.color,
                          }}
                        />
                      </div>
                      <div className="text-xs font-bold" style={{ color: bar.color }}>{bar.value}/{bar.max}</div>
                    </div>
                  ))}
                </div>

                {/* Recommended paths */}
                {recommendation!.paths.length > 0 && (
                  <div className="mb-4">
                    <div className="text-xs font-bold text-gray-700 mb-2">{t('推荐路径', 'Recommended Paths')}</div>
                    <div className="space-y-2">
                      {recommendation!.paths.map((path) => (
                        <div key={path.name} className="flex items-start gap-2 p-3 rounded-xl" style={{ background: `${path.color}10`, border: `1px solid ${path.color}25` }}>
                          <i className="ri-route-line text-sm mt-0.5" style={{ color: path.color }}></i>
                          <div>
                            <div className="text-xs font-bold" style={{ color: path.color }}>{path.name}</div>
                            <div className="text-xs text-gray-600 mt-0.5">{path.desc}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Next steps */}
                <div className="mb-5">
                  <div className="text-xs font-bold text-gray-700 mb-2">{t('建议下一步', 'Suggested Next Steps')}</div>
                  <div className="space-y-1.5">
                    {recommendation!.nextSteps.map((step, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-gray-600">
                        <span className="w-4 h-4 flex items-center justify-center rounded-full bg-[#7C3AED]/10 text-[#7C3AED] font-bold shrink-0 mt-0.5 text-[10px]">{i + 1}</span>
                        {step}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={reset}
                    className="flex-1 py-2.5 rounded-full text-sm font-bold border border-gray-200 text-gray-600 hover:border-[#7C3AED] hover:text-[#7C3AED] cursor-pointer transition-all whitespace-nowrap"
                  >
                    {t('重新测试', 'Retake Test')}
                  </button>
                  <button
                    onClick={() => setOpen(false)}
                    className="flex-1 py-2.5 rounded-full text-sm font-bold text-white cursor-pointer transition-all whitespace-nowrap"
                    style={{ background: 'linear-gradient(135deg, #7C3AED, #A78BFA)' }}
                  >
                    {t('查看详细指南', 'View Detailed Guide')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
